// Searchable, server-paged picker for the large master lists (vendors ~2,700).
// Used in the Import Control edit modal for vendor / company / GL code.
// On pick it returns both the id and the canonical name (app.md §9 step 5).
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import {
  Combobox,
  Option,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { useFieldLabelName } from '../lib/hooks'
import { popupPositionProps } from './popupPositioning'
import { shouldAdoptUpstreamValue } from '../lib/pickerResync'
import { decideOnEnter, decideOnLeave, listNeedsReset, type PickerCommitState, type PickerOutcome } from '../lib/pickerCommit'
import { rankBySearch } from '../lib/optionSearch'

/**
 * WHAT AN EMPTY ACCOUNT OR DEPARTMENT PICKER ON A LINE ITEM SAYS, at every
 * step (owner, 2026-09-30 — `docs/decisions.md` D-242: *"Let's just always
 * use "Search" without the ellipsis for both empty Accounts and empty
 * Departments. That goes for invoice modals at other steps, too."*). Passed
 * explicitly by those pickers; every other picker keeps the default below.
 */
export const CODING_PICKER_PLACEHOLDER = 'Search'

export interface EntityOption {
  id: string
  primary: string
  secondary?: string
  /**
   * What a pick writes into the box, when it is not `primary` — an option
   * whose label says what it does, as *New Group: Poultry* does, puts only the
   * name it stands for in the box (owner, 2026-10-23 — D-411: the label was
   * landing in the field and being searched for as a name).
   */
  boxText?: string
}

interface Props {
  /** Current selection (id + name) if any. */
  value?: { id?: string; name?: string }
  search: (term: string) => Promise<EntityOption[]>
  onSelect: (option: EntityOption) => void
  placeholder?: string
  disabled?: boolean
  /** Match surrounding controls (e.g. small inputs in the line items table). */
  size?: 'small' | 'medium' | 'large'
  /**
   * A persistent, always-visible label above the field, wired as the
   * Combobox's accessible name (`<label htmlFor>` against the input's own
   * id — `useId`-generated, same value used for the anti-autofill `name` —
   * and, since QA 2026-09-30 C1-05, the same words as the input's
   * `aria-label`, so the name never falls back to the placeholder).
   * Without this, the field's only name is its placeholder, which is exactly
   * what makes two or three unlabeled pickers stacked in one cell read as one
   * thing rather than two or three questions (the "should be two dropdowns"
   * report) — and disappears once a value is picked, whether or not another
   * label existed.
   *
   * Omit it where the caller already supplies a name some other way — a
   * surrounding Fluent `<Field label>` (Company/Vendor above,
   * `AssignmentsScreen`'s "Person"). `Field` renders its own
   * `<label htmlFor="field-«rN»__control">` and, through
   * `useFieldControlProps_unstable`, stamps that SAME generated id onto the
   * control's own input; it deliberately does NOT also set `aria-labelledby`,
   * because `useCombobox` reports `supportsLabelFor: true` and `Field` trusts
   * the id/htmlFor pairing alone. That is exactly why the `id` below is
   * conditional on `label` rather than unconditional: setting our own `id`
   * whenever this prop is omitted would win the merge in `inputSlotProps`'s
   * caller below (ours is spread last) and steal the input's id out from
   * under Field's `<label>`, which would then point at nothing — losing the
   * accessible name Field was supposed to provide, not gaining one.
   */
  label?: string
  /**
   * An accessible name with no visible chrome, for the rows that have no
   * vertical room for `label`. `AssociatedEntitiesEditor`'s grid is one line
   * with `alignItems: center`, and a caption there pushes the input below the
   * source text it is aligned with. It also lets the name say what the FIELD
   * does rather than repeating a word already on screen: the row's first cell
   * already reads "Vehicle", so the picker beside it wants "Vehicle to tag",
   * not "Vehicle" a second time.
   *
   * Sets `aria-label` only — no `id`, so a surrounding `Field` keeps its
   * `htmlFor` pairing. Pass this OR `label`, never both: two names on one
   * control is the defect the conditional `id` above exists to prevent.
   */
  ariaLabel?: string
  /**
   * Where to mount the listbox, overriding self-resolution (below). Pass the
   * enclosing `DialogSurface` element explicitly when the picker's own lookup
   * would be wrong or premature for the caller's timing (see
   * `AssignmentsScreen`, which keeps its own `dialogSurface` ref and passes it
   * here — redundant with self-resolution once the ref has fired, but harmless,
   * since an explicit value always wins). Passing `undefined` (the default) lets
   * `EntityPicker` find its own dialog host; passing `null` forces the inline
   * fallback even inside a dialog.
   */
  mountNode?: HTMLElement | null
  /**
   * An opaque key naming the value the option list depends on, for callers
   * whose `search` closure varies with something other than this picker's
   * own query text — e.g. `InvoiceLineItemsTable`'s "Who is it for?"
   * department picker, whose `search` closes over the row's `account_id`
   * and is passed fresh on every render regardless of whether that id
   * changed. `search` FUNCTION IDENTITY is therefore not a usable signal —
   * depending on it would refetch on every render, not just on an actual
   * account change — so this key exists for the caller to name the one
   * thing that actually varies. The row's own `key` can't substitute either:
   * `InvoiceLineItemsTable` keys each row by `c.id` alone (stable across an
   * account edit), which is exactly why this picker instance — and its
   * lazy first-fetch guard below — survives the edit and goes stale.
   *
   * Optional and inert when omitted: every other picker in the app (Vendor,
   * Company, Account, Vehicle, Person) doesn't vary this way and passes
   * nothing here, so this prop changes no behavior for them.
   */
  optionsKey?: string
  /**
   * Marks the control invalid — a red border via Fluent's native
   * `[aria-invalid]` styling PLUS the screen-reader state, together (owner
   * item 8, 2026-08-18). Set on the CONTROL, never on a hand-rolled border
   * around the surrounding cell: `EditableInvoiceForm.tsx` used to tint the
   * `TableCell` red (`cellFlagged`) behind a line's "What is it?" picker, but
   * the Combobox paints an OPAQUE `colorNeutralBackground1` at 100% width, so
   * the tint survived only as a hairline in the cell's padding — invisible in
   * the case that matters (a reviewer who resolved every flag and still
   * couldn't tell why Release stayed disabled). `aria-invalid` on the
   * control itself cannot be painted over by the control.
   */
  invalid?: boolean
  /**
   * Read-only / "locked" treatment (owner item 13, 2026-08-18) — matches
   * every OTHER locked control on the same screens: `appearance:
   * 'filled-darker'`, normal (not disabled) foreground, still focusable and
   * text-selectable. That is Fluent `Input`'s `readOnly` + `filled-darker`
   * pairing, used everywhere else a field locks (External ID, the amounts,
   * every `InvoiceHeaderPanel` field) — but `Combobox` has no `readOnly` prop
   * to match it with, so this assembles the same effect from three parts:
   * the underlying input gets the HTML `readOnly` attribute (blocks typing,
   * keeps focus and text selection), `open` is pinned permanently closed
   * (there is nothing to expand — a locked picker offers no new pick, so a
   * chevron that drops an inert listbox would be furniture), and `appearance`
   * is forced to `'filled-darker'`. This is a DIFFERENT thing from `disabled`,
   * which stays Fluent's own "temporarily unavailable" look
   * (`colorNeutralBackgroundDisabled`, `cursor: not-allowed`, not focusable)
   * — the right shape for an in-flight save or a picker gated on another
   * field being set first, never for "this step doesn't let anyone edit
   * this." Pass this OR `disabled`, never both.
   */
  locked?: boolean
  /** An extra class on the Combobox, merged after this picker's own — the
      bulk row's mode picker takes a brand fill this way (D-242). */
  className?: string
  /**
   * THE PICKER CAN BE EMPTIED (QA 2026-10-04, C1-01/C1-02 — D-295). Given, an
   * emptied box clears the value when the reviewer leaves it or presses Enter,
   * and a clear button (×) shows while a value is set. Omitted, an emptied box
   * puts the stored value back. Never a pick of the first option either way —
   * `lib/pickerCommit.ts` owns that rule.
   */
  onClear?: () => void
  /** The clear button's name, `Clear <this>`. Defaults to `ariaLabel` or `label`. */
  clearLabel?: string
  /** `false` where the caller already draws its own clear button beside the
      picker (Upload's Company, the Rules Department), so there are not two. */
  clearButton?: boolean
  /**
   * A SAVE IS IN FLIGHT (QA 2026-10-04, C2-06). The box takes no typing and
   * its list stays shut, but it stays focusable — unlike `disabled`. A picker
   * disabled for its line's save threw focus to the page: Tab out of a saved
   * Account aimed at the Department, which was disabled by the time focus
   * arrived, and Enter on the Department disabled the very box that had focus.
   * Use this, not `disabled`, for the length of a save.
   */
  busy?: boolean
  /**
   * NO PICK IS POSSIBLE HERE NOW, AND FOCUS IS NEVER THROWN (QA 2026-10-04,
   * review of C2-06). Looks and reads as disabled (`aria-disabled`, dimmed,
   * out of the Tab order) but the input is never the native `disabled`: an
   * Account saved with Tab to a capital-only account the line's total does
   * not allow blocks the Department after focus has landed there, and a
   * natively disabled box would drop that focus to the page. Use this, not
   * `disabled`, where the block can arrive while the box has focus.
   */
  blocked?: boolean
  /** The `id` of a message that describes this field now — a refused save's
      reason (QA 2026-10-04, C1-09). Set as the input's `aria-describedby`. */
  describedBy?: string
}

// Empty-term ("top results") responses shared across pickers with the same
// search fn, so opening a many-line invoice doesn't fire one query per picker.
// Keyed weakly by the fn identity; entries drop when the fn is GC'd.
const emptyResultsCache = new WeakMap<Props['search'], EntityOption[]>()

const useStyles = makeStyles({
  hint: { padding: '6px 12px', color: tokens.colorNeutralForeground3 },
  // Wraps the label (when given) and the Combobox. Always present, whether or
  // not `label` is, so the DOM shape doesn't shift between the two — width and
  // shrink behavior mirror `combobox` below so a caller that used to size the
  // Combobox directly (`pickerStack`, `bulkPicker`) sizes this the same way.
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, width: '100%' },
  // Same 12px caption size as the picker's own text (size="small" Combobox).
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  // Fluent's Combobox defaults to ~250px min-width; clear it so the picker fills
  // its container (e.g. a line item's Account cell) instead of overrunning the next.
  // The root is an inline-grid of [input 1fr | chevron auto]; without min-width 0
  // the input can't shrink below its intrinsic width, which pushes the chevron
  // outside the root's border in narrow containers.
  combobox: {
    width: '100%',
    minWidth: 0,
    '& input': { minWidth: 0, width: '100%' },
  },
  // This styling is for the FALLBACK path now: `EntityPicker` resolves its own
  // dialog host (below) and portals there via `mountNode` whenever one exists,
  // so most instances never render this inline at all. It still has to look
  // right when one doesn't — no dialog ancestor, or the ref hasn't resolved
  // yet — and inline is where the narrow (Account/Company) cell width and
  // truncated option text used to show up. Give it a comfortable min-width so
  // names + ids are readable; it overlays neighboring columns like any
  // dropdown.
  //
  // The rest is legibility, reported from the Assignments dialog back when
  // inline was the only path: an INLINE listbox inherits its container's
  // surface, so inside a dialog it blended into the dialog and ran past the
  // content box unbounded. An explicit raised surface plus a bounded,
  // self-scrolling height makes it read as a dropdown wherever it opens.
  listbox: {
    minWidth: '340px',
    maxWidth: '92vw',
    // Owns the height, because `autoSize` is off in `positioning` below.
    maxHeight: '280px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    // High, not 1: when inline (the fallback — no dialog host resolved) the
    // listbox shares a stacking context with the fields it overlays and with
    // anything below it in the dialog, and a z-index of 1 loses to any of them
    // that is itself positioned. Reported as "the z-index is off" — the options
    // rendered, underneath. Needed just as much when mounted at a resolved
    // dialog host, NOT only in the inline fallback: an explicit `mountNode`
    // disables Fluent's `usePortal` from supplying its own mount node, and
    // `usePortalMountNodeStyles` — the only place Fluent sets `zIndex: 1000000`
    // — fires only for that self-supplied node. A listbox mounted at the
    // dialog surface is therefore a z-auto sibling of anything else raised in
    // that same dialog (the tag-entities Popover, the line pop-ups' `surface` at
    // `zIndex: 10` in `LineItemIconButton.tsx`, or the sticky action bar at
    // `zIndex: 2`) and loses without a layer of its own.
    zIndex: 1000,
  },
  // `blocked`: Fluent's own disabled look, drawn by hand because the input
  // must not be natively disabled.
  blocked: {
    cursor: 'not-allowed',
    backgroundColor: tokens.colorTransparentBackground,
    borderTopColor: tokens.colorNeutralStrokeDisabled,
    borderRightColor: tokens.colorNeutralStrokeDisabled,
    borderBottomColor: tokens.colorNeutralStrokeDisabled,
    borderLeftColor: tokens.colorNeutralStrokeDisabled,
    '::after': { content: 'none' },
    '& input': { color: tokens.colorNeutralForegroundDisabled, cursor: 'not-allowed' },
    '& input::placeholder': { color: tokens.colorNeutralForegroundDisabled },
    '& [aria-hidden="true"]': { color: tokens.colorNeutralForegroundDisabled },
  },
  // Two lines per row need room to separate; Fluent's default option padding is
  // tuned for one.
  option: { paddingTop: '6px', paddingBottom: '6px' },
  optionPrimary: { color: tokens.colorNeutralForeground1 },
  optionSecondary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

/**
 * Cast because the slot's type admits no `data-*` attributes, and these two are
 * the documented opt-outs for LastPass and Dashlane — attributes those
 * extensions read off the DOM, not React props with a declared type.
 */
function inputSlotProps(name: string): Record<string, unknown> {
  return {
    autoComplete: 'off',
    spellCheck: false,
    name,
    'data-lpignore': 'true',
    'data-form-type': 'other',
  }
}

export function EntityPicker({ value, search, onSelect, placeholder, disabled, size, mountNode, label, ariaLabel, optionsKey, invalid, locked, className, onClear, clearLabel, clearButton = true, busy = false, blocked = false, describedBy }: Props) {
  const s = useStyles()
  // The surrounding Fluent `Field`'s label, as this input's name (QA
  // 2026-10-04, A-08: the Rules dialogs' pickers read as their placeholders,
  // "e.g. Supplies", rather than "default that line item's Account to").
  // Empty when `label` or `ariaLabel` names the field instead.
  const fieldLabelName = useFieldLabelName(label || ariaLabel ? { 'aria-label': label ?? ariaLabel } : {})
  // The dialog host this picker resolves for itself, when the caller doesn't
  // hand one in. Read off the Combobox's OWN forwarded ref (`triggerRef`
  // below) — never a wrapper div: a wrapper wrapping the control here would
  // re-parent it inside `pickerStack` (InvoiceLineItemsTable) and `picker`
  // (`LineItemBulkBar`), breaking the flex-shrink fix `s.combobox` above exists for.
  // `role="dialog"` only. `DialogSurface` can also emit `role="alertdialog"`
  // for `modalType="alert"`, but no dialog in this app uses that mode today —
  // CLAUDE.md's "build nothing speculative" rule, so this does not add a
  // second selector arm for a case nothing exercises. Add it back in the same
  // commit that adds the first `modalType="alert"` dialog.
  const triggerRef = useRef<HTMLInputElement>(null)
  const [selfMountNode, setSelfMountNode] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSelfMountNode((triggerRef.current?.closest('[role="dialog"]') as HTMLElement | null) ?? null)
  }, [])
  // An explicit `mountNode` prop (even `null`, e.g. `AssignmentsScreen`'s ref
  // before it fires) always wins over self-resolution — only `undefined`
  // (the prop omitted) defers to it.
  const effectiveMountNode = mountNode !== undefined ? mountNode : selfMountNode
  // Defeats Chrome's field-name heuristic (see the `input` slot below): with a
  // per-instance nonsense name there is no "Person"-shaped field to match saved
  // form history against.
  const fieldName = `entity-search-${useId()}`
  const valueName = value?.name ?? ''
  const [query, setQuery] = useState(valueName)
  // The options, WITH the term they answer. The term is what makes a stale
  // result safe: a search for a new term leaves the previous term's rows in
  // `options` until its own promise settles, and those rows stayed clickable
  // under the "Searching…" spinner while the box already read something else —
  // so a reviewer who typed a new vendor and clicked the first row committed a
  // vendor from the query before it.
  const [options, setOptions] = useState<EntityOption[]>([])
  const [optionsTerm, setOptionsTerm] = useState<string | null>(null)
  // The last term the reviewer TYPED, or null when the box is showing a value
  // they did not type (a fresh open, or a pick). State rather than a ref
  // because `shownOptions` reads it during render.
  const [typedTerm, setTypedTerm] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // Only the latest search may write options — a slow earlier response (e.g. the
  // first open-term query) must not overwrite a newer one.
  const searchSeq = useRef(0)
  // The term the latest fetch is asking for, until it answers; null when none
  // is running (QA 2026-10-04, review of A-07). A reset while the unfiltered
  // fetch is still out would otherwise send a second, identical one.
  const inFlightTerm = useRef<string | null>(null)
  const didInitialFetch = useRef(false) // lazy: first fetch waits for focus/open
  const editing = useRef(false) // input focused — don't clobber in-progress typing
  const typedWithoutSelect = useRef(false) // freeform text not yet committed to a pick
  // Set by a pick, cleared by the resync below. A pick writes the OPTION's own
  // `primary` into the box, but several callers compose a richer display name
  // than the option carries — the Marketing category sends
  // back `A — Newspaper ad` where the option's `primary` is the bare code `A`,
  // and the code alone is exactly what those fields must never show on their
  // own. The resync effect is what would deliver that composed name, and it
  // deliberately stands down while the field has focus so it cannot clobber
  // in-progress typing — but a pick IS the end of typing, so the very next
  // value it produces has to be adopted, focus or not.
  const adoptNextValue = useRef(false)
  // THE PICKER'S OWN KEYBOARD RULE (QA 2026-10-04, C1-01/C1-03 — D-295). The
  // list's open state is ours, so Enter can close it after a save; `navigated`
  // is whether the arrow keys moved the highlight since the last keystroke;
  // `tabbing` stands Fluent's own Tab-selects-the-highlight down for the length
  // of one Tab key press; `pendingEnter` is an Enter pressed before the matches
  // for its text had arrived.
  const [open, setOpen] = useState(false)
  // Typing and the list are off while busy, as while locked; focus is not.
  const inert = locked || busy || blocked
  const clearable = !inert && !!onClear && clearButton
  const navigated = useRef(false)
  const tabbing = useRef(false)
  const pendingEnter = useRef<string | null>(null)
  const activeId = useRef<string | null>(null)

  const runSearch = useCallback(
    (term: string) => {
      const my = ++searchSeq.current
      const cached = term === '' ? emptyResultsCache.get(search) : undefined
      if (cached) {
        inFlightTerm.current = null
        setOptions(cached)
        setOptionsTerm(term)
        setLoading(false)
        return
      }
      inFlightTerm.current = term
      setLoading(true)
      search(term)
        .then((opts) => {
          if (term === '') emptyResultsCache.set(search, opts)
          if (my === searchSeq.current) {
            setOptions(opts)
            setOptionsTerm(term)
          }
        })
        .catch(() => { if (my === searchSeq.current) { setOptions([]); setOptionsTerm(term) } })
        .finally(() => {
          if (my === searchSeq.current) {
            inFlightTerm.current = null
            setLoading(false)
          }
        })
    },
    [search],
  )

  // Resync the displayed text when the selection changes upstream (e.g. a header
  // company change repoints inheriting line items) — but never over the top of
  // typing somebody has not committed.
  //
  // The decision itself is `shouldAdoptUpstreamValue` in `lib/pickerResync.ts`,
  // out here because this suite has no DOM and the rule could not otherwise be
  // tested. Its header carries the reasoning, including why UNCOMMITTED TYPING
  // is the thing that holds it back and focus alone is not (QA
  // re-verification, 2026-09-22, N-02: every dialog focuses its first control
  // as it opens, so a picker that stood down on focus never took the seed its
  // own dialog was handing it, and showed the previous row's value).
  useEffect(() => {
    if (
      shouldAdoptUpstreamValue({
        editing: editing.current,
        typedWithoutSelect: typedWithoutSelect.current,
        valueName,
        adoptNextValue: adoptNextValue.current,
      })
    ) {
      adoptNextValue.current = false
      setQuery(valueName)
      typedWithoutSelect.current = false
      setTypedTerm(null)
    }
  }, [valueName])

  // Invalidate the cached empty-term option list when the caller says the
  // thing it depends on changed (`optionsKey`) — e.g. the row's account
  // for the department picker. Fires only on an actual key change (the ref
  // starts equal to the initial key, so mount is a no-op) and never when the
  // prop isn't used at all. Re-arms the lazy first-fetch guard so the NEXT
  // focus/open does a real fetch instead of the `didInitialFetch` no-op, and
  // wipes the stale list now rather than leaving the old key's options
  // sitting there for the instant before that fetch resolves. `runSearch`'s
  // own `searchSeq` ordering guard (above) still applies once that fetch
  // fires, so a late response from a fetch superseded by yet another key
  // change can't land on top of a newer one.
  const lastOptionsKey = useRef(optionsKey)
  useEffect(() => {
    if (optionsKey === undefined || optionsKey === lastOptionsKey.current) return
    lastOptionsKey.current = optionsKey
    didInitialFetch.current = false
    setOptions([])
  }, [optionsKey])

  // Clear a pending debounce on unmount.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  function ensureInitialFetch() {
    if (didInitialFetch.current) return
    didInitialFetch.current = true
    runSearch('')
  }

  function onInput(text: string) {
    setQuery(text)
    typedWithoutSelect.current = true
    navigated.current = false
    pendingEnter.current = null
    // TEXT OPENS THE LIST, however it arrived (QA 2026-10-04, C1-03). Fluent
    // opens it on a keydown only, so a paste, or text typed with no keydown
    // (the QA browser's typing sends none), left it shut — and Enter on a shut
    // list only opened it.
    setOpen(true)
    setTypedTerm(text)
    if (timer.current) clearTimeout(timer.current)
    // "Searching…" from the KEYSTROKE, not from the fetch 300 ms later:
    // `shownOptions` hides the previous term's rows the moment the box says
    // something else, and without this the gap would read as "No matches."
    // for the length of the debounce.
    setLoading(true)
    timer.current = setTimeout(() => runSearch(text), 300)
  }

  /** The options, but only while they answer what the box actually says.
      Typing is the trigger: a picker opened on an existing value shows the
      open-term list (`runSearch('')`) against a box holding that value's name,
      and that pairing is correct — it is only once the reviewer has typed that
      an option list from an earlier term becomes a wrong answer to the
      question on screen. */
  const answering = typedTerm !== null && optionsTerm !== null && optionsTerm !== typedTerm ? [] : options
  // BEST MATCH FIRST (QA 2026-10-04, C1-04): exact, then starts-with, then a
  // word starting with it, then the rest — by name or by ID, the ID being the
  // first part of the second line (`449 · Altoona, PA`). `lib/optionSearch.ts`
  // owns the order; the server's rows arrive in name order.
  const shownOptions = rankBySearch(answering, typedTerm, (o) => [o.primary, o.secondary?.split(' · ')[0]])

  /** Where the box's decision starts from: what it says, and what is shown. */
  function commitState(): PickerCommitState<EntityOption> {
    // The highlight as the input itself names it (`aria-activedescendant`), so
    // it is always the one on screen; the last change event as a fallback.
    const activeEl = triggerRef.current?.getAttribute('aria-activedescendant')
    const fromDom = activeEl ? document.getElementById(activeEl)?.getAttribute('data-option-id') : null
    return {
      text: query,
      typed: typedWithoutSelect.current,
      navigated: navigated.current,
      activeId: fromDom ?? activeId.current,
      options: shownOptions,
      optionsAnswerText: !loading && typedTerm !== null && optionsTerm === typedTerm,
      clearable: !!onClear,
      storedId: value?.id,
    }
  }

  function pick(opt: EntityOption) {
    typedWithoutSelect.current = false
    navigated.current = false
    pendingEnter.current = null
    setTypedTerm(null)
    adoptNextValue.current = true
    setQuery(opt.boxText ?? opt.primary)
    setOpen(false)
    resetSearch()
    onSelect(opt)
  }

  function clear() {
    typedWithoutSelect.current = false
    navigated.current = false
    pendingEnter.current = null
    setTypedTerm(null)
    adoptNextValue.current = true
    setQuery('')
    setOpen(false)
    resetSearch()
    onClear?.()
  }

  /** THE LIST GOES BACK TO UNFILTERED (QA 2026-10-04, A-07). Once the box
      no longer shows typed text — a pick, a clear, the stored value put back —
      the list must not stay filtered to words that are gone: an empty Vendor
      box reopened on the two `3 GEMS` vendors and no "Any vendor". A pending
      search for the discarded text is dropped too. `lib/pickerCommit.ts`
      decides whether a fresh unfiltered fetch is owed. */
  function resetSearch() {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = undefined
    }
    if (listNeedsReset({ fetched: didInitialFetch.current, optionsTerm, inFlightTerm: inFlightTerm.current })) runSearch('')
    // Nothing is fetching, so "Searching…" from the dropped keystroke ends.
    else if (inFlightTerm.current === null) setLoading(false)
  }

  /** The box goes back to the stored value. */
  function restore() {
    typedWithoutSelect.current = false
    navigated.current = false
    pendingEnter.current = null
    setTypedTerm(null)
    setQuery(valueName)
    resetSearch()
  }

  function apply(outcome: PickerOutcome<EntityOption>, onKeep: () => void) {
    // The stored value chosen again is no change: nothing to save.
    if (outcome.kind === 'commit' && outcome.option.id === value?.id) {
      restore()
      setOpen(false)
    } else if (outcome.kind === 'commit') pick(outcome.option)
    else if (outcome.kind === 'clear') clear()
    else if (outcome.kind === 'keep') onKeep()
  }

  // AN ENTER THAT CAME BEFORE ITS MATCHES (QA 2026-10-04, C1-03): saved the
  // moment they arrive, unless the reviewer has typed on or left since. Never
  // while the line saves (review of C2-06): the pending save waits for that one
  // to finish, so two never run at once.
  useEffect(() => {
    if (pendingEnter.current === null || loading || busy) return
    if (typedTerm !== pendingEnter.current || optionsTerm !== typedTerm) return
    pendingEnter.current = null
    apply(decideOnEnter(commitState()), () => {})
    // `commitState` reads this render's values; the trigger is the answer landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, optionsTerm, typedTerm, options, busy])

  function onKeyDownCapture(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (locked || disabled) return
    // Busy or blocked: no key reaches Fluent or the save rule, except Tab to move on.
    if (busy || blocked) {
      if (e.key !== 'Tab') {
        e.preventDefault()
        e.stopPropagation()
      }
      return
    }
    if (e.altKey || e.ctrlKey || e.metaKey) return
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
      case 'PageDown':
      case 'PageUp':
        if (open) navigated.current = true
        return
      case 'Tab':
        // Fluent saves the highlighted option on Tab; leaving decides instead.
        tabbing.current = true
        return
      case ' ':
        // SPACE TYPES A SPACE (QA 2026-10-04, review of C1-01). On a list
        // opened by a click or Alt+Down with nothing typed, Fluent reads Space
        // as "close and select" and saves the highlighted option — for Vendor,
        // the list's first entry. Kept from Fluent unless the arrow keys
        // chose an option; the space then lands in the box and `onInput`
        // takes it from there.
        if (!navigated.current) e.stopPropagation()
        return
      case 'Enter': {
        if (e.shiftKey) return
        // Closed and untouched, Enter is Fluent's: it opens the list.
        if (!open && !typedWithoutSelect.current) return
        e.preventDefault()
        e.stopPropagation()
        const outcome = decideOnEnter(commitState())
        if (outcome.kind === 'wait') pendingEnter.current = query
        else apply(outcome, () => setOpen(false))
        return
      }
    }
  }

  return (
    <div
      className={s.fieldWrap}
      onKeyDownCapture={onKeyDownCapture}
      onKeyDown={() => { tabbing.current = false }}
    >
    {label && (
      <label htmlFor={fieldName} className={s.fieldLabel}>
        {label}
      </label>
    )}
    <Combobox
      // The listbox's home is resolved above (`effectiveMountNode`), not decided
      // here — this component no longer assumes inline is the default. A
      // resolved dialog host keeps the popup inside that dialog's focus trap
      // (portaled to `document.body`, the Fluent default, it would sit outside
      // the trap and tabster would haul focus back, slamming it shut —
      // intermittently, since it only bites when a re-render or the async open
      // race lands mid-interaction) AND mounts it at the surface's own stacking
      // level, above the fields it would otherwise overlay. No host — nothing
      // outside a dialog, or the ref hasn't resolved yet — falls back to
      // `inlinePopup`, which is now the exception case, not the norm; the same
      // two-line rationale covers `EditableInvoiceForm`'s Dropdown,
      // `AssociatedEntitiesEditor`'s Dropdown and `TrainingScreen`'s freeform
      // Combobox, which call `popupPositionProps` directly rather than
      // duplicating it.
      //
      // `positioning` rides along unconditionally inside that same helper: it is
      // not compensation for staying inline, it is what a portaled popup needs
      // too, since `autoSize` collapses either one to a single scrolling row
      // without it (reported from Assignments, 2026-08-06; screenshot
      // confirmed). Measuring against the window and turning `autoSize` off
      // means the height comes from the stylesheet (`s.listbox`) rather than
      // from whatever space happens to be left over.
      ref={triggerRef}
      // The BROWSER's own autofill dropdown, suppressed. Fluent sets no
      // `autocomplete` on the trigger input, so Chrome treats a field labelled
      // "Person" with placeholder "Search staff…" as a name field and offers its
      // saved form history, over the top of the real listbox. `autocomplete=off`
      // alone is advisory for name-shaped fields, so the per-instance `name`
      // removes the shape as well; the two `data-` attributes are the documented
      // opt-outs for LastPass and Dashlane. NOT `new-password`, which suppresses
      // reliably but makes Chrome offer to generate a password in a staff search.
      input={{
        ...inputSlotProps(fieldName),
        // `label` also sets `aria-label` to the same words (QA 2026-09-30,
        // C1-05): the bulk bar's two pickers were read as "Search…" and
        // "Set "Account" first", their placeholders, although the `<label
        // htmlFor>` pairing was in place. One name either way, the visible one.
        ...(label ? { id: fieldName, 'aria-label': label } : {}),
        ...(!label && ariaLabel ? { 'aria-label': ariaLabel } : {}),
        ...fieldLabelName,
        // `locked`: the HTML attribute itself, so the native input blocks
        // typing while staying focusable and text-selectable — the same
        // `readOnly` Fluent `Input` uses for every other locked box.
        ...(inert ? { readOnly: true } : {}),
        ...(busy ? { 'aria-busy': true } : {}),
        // `blocked`: disabled to the reader and out of the Tab order, but still
        // able to hold focus that has already arrived.
        ...(blocked ? { 'aria-disabled': true, tabIndex: -1 } : {}),
        // `invalid`: on the input, not just the Combobox root, so screen
        // readers get the state along with the border (see the `invalid` prop
        // doc above).
        ...(invalid ? { 'aria-invalid': true } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
      }}
      {...popupPositionProps(effectiveMountNode)}
      listbox={{
        className: s.listbox,
        // THE MOUSE-PICK RACE. `Option`'s own root (@fluentui/react-combobox)
        // is a plain, non-focusable `<div>` with no `onMouseDown` of its own,
        // so a mouse pick is: mousedown on the option (which some browsers
        // resolve by blurring the currently-focused input, since the
        // mousedown's target isn't focusable) → our own `onBlur` below fires
        // and unconditionally resets the box to `valueName` (correct on a
        // genuine blur, but here it lands BEFORE `onOptionSelect`) → mouseup
        // → click → `onOptionSelect` fires and calls `setQuery`/`onSelect`.
        // The click-driven pick and the blur-driven reset are two separate
        // native events, so which one a browser fires — and thus whether the
        // pick sticks — depends on that browser's own focus-on-mousedown
        // behavior for a non-focusable target, not on anything this component
        // controls. `preventDefault` on the listbox's mousedown is the
        // standard fix for exactly this shape of control (the same guard
        // MUI's Autocomplete and downshift's listbox both carry): it stops
        // the browser from ever treating the click as a focus change, so the
        // input never blurs and the click's own `onOptionSelect` is the only
        // thing that runs. A mouse pick must stick regardless of which
        // browser is doing the clicking.
        //
        // SCOPED to option targets, not the whole listbox: this same element
        // is its own scroll container (`maxHeight` + `overflowY: 'auto'`
        // above), and in Chromium a mousedown on a native scrollbar thumb is
        // dispatched with the scrollable element itself as the target — an
        // unconditional `preventDefault` here also swallows the scrollbar
        // drag, breaking it for every render site (the vendor picker's
        // ~50-result, two-line-option list overflows 280px on nearly every
        // search). Guard on the event target being an option rather than the
        // listbox itself, so the listbox's native scroll behavior survives.
        onMouseDown: (e) => {
          if ((e.target as HTMLElement).closest('[role="option"]')) e.preventDefault()
        },
      }}
      className={mergeClasses(s.combobox, blocked && s.blocked, className)}
      appearance={locked ? 'filled-darker' : undefined}
      // Pinned permanently closed while locked — nothing to expand, since a
      // locked picker offers no new pick. Ours elsewhere, so a save on Enter
      // can close it (D-295).
      open={inert ? false : open}
      onActiveOptionChange={(_, data) => { activeId.current = data.nextOption?.value ?? null }}
      // THE CLEAR BUTTON (QA 2026-10-04, C1-02): only where the value may be
      // emptied. Fluent draws it as an `aria-hidden` icon; it is a named
      // button here, so it can be found and pressed by name.
      clearable={clearable}
      clearIcon={
        clearable
          ? {
              'aria-hidden': undefined,
              role: 'button',
              'aria-label': `Clear ${clearLabel ?? ariaLabel ?? label ?? 'value'}`,
              title: 'Clear',
              onClick: () => clear(),
            }
          : undefined
      }
      size={size}
      disabled={disabled}
      placeholder={placeholder ?? 'Search…'}
      value={query}
      selectedOptions={value?.id ? [value.id] : []}
      onFocus={() => { editing.current = true; if (!inert) ensureInitialFetch() }}
      onOpenChange={(_, data) => {
        if (inert) return
        setOpen(data.open)
        if (data.open) ensureInitialFetch()
      }}
      onBlur={() => {
        editing.current = false
        tabbing.current = false
        // LEAVING SAVES ONLY WHAT WAS CHOSEN (D-295): an option moved to with
        // the arrow keys, or the one exact or only match; an emptied box
        // clears where it may. Anything else falls through to the restore.
        const outcome = decideOnLeave(commitState())
        if (outcome.kind === 'commit' || outcome.kind === 'clear') {
          apply(outcome, () => {})
          return
        }
        // THE BOX SHOWS WHAT IS STORED. Unconditional, not just for freeform
        // text the user typed and never committed: a SELECTED option the server
        // then REFUSED left the picked words sitting in the field while the
        // record still held the old value, because the refusal path returns
        // without reloading (`InvoiceDetailModal`'s half-write handlers) and so
        // `valueName` never changes for the resync effect above to notice. The
        // reviewer was reading one account and about to approve another.
        // When the pick DID land, `valueName` is already the picked name and
        // this is a no-op.
        restore()
      }}
      onInput={(e) => onInput((e.target as HTMLInputElement).value)}
      onOptionSelect={(_, data) => {
        // Fluent's Tab pick stands down: leaving decides (`onBlur`).
        if (tabbing.current) return
        // NO OPTION IS NOT A CLEAR. Fluent reports an empty selection whenever
        // the typed text stops matching the stored option — on the first
        // keystroke over a selected value — and on its clear icon. Only the
        // icon clears, through its own `onClick` above.
        if (data.optionValue === undefined) return
        const opt = shownOptions.find((o) => o.id === data.optionValue)
        if (opt) pick(opt)
      }}
      freeform
    >
      {loading && (
        <div className={s.hint}>
          <Spinner size="tiny" label="Searching…" />
        </div>
      )}
      {!loading && shownOptions.length === 0 && (
        <div className={s.hint}>No matches.</div>
      )}
      {shownOptions.map((o) => (
        <Option key={o.id} value={o.id} text={o.boxText ?? o.primary} className={s.option} data-option-id={o.id}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className={s.optionPrimary}>{o.primary}</span>
            {o.secondary && <span className={s.optionSecondary}>{o.secondary}</span>}
          </div>
        </Option>
      ))}
    </Combobox>
    </div>
  )
}
